import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {IProduct, IFamily} from '../../_models/drilldown';
import {sprintf} from 'sprintf-js';
import {Log} from 'ng2-logger';
import {ToastsManager} from 'ng2-toastr';
import {DrilldownService} from '../../_services/drilldown.service';
import {AdminService} from '../../_services/admin.service';
import {Subscription} from 'rxjs/Subscription';
import {Subject} from 'rxjs/Subject';
import {environment} from '../../../environments/environment';

import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import {ACLFeature} from '../../_models/acl-feature';
import {Permissions} from '../../_services/permissions';
import Util from '../../util';

const log = Log.create('DrilldownList');

@Component({
  moduleId: module.id,
  styleUrls: ['list.less'],
  templateUrl: 'list.component.html'
})

export class DrilldownListComponent implements OnInit, OnDestroy {

  BASE_URL: string = environment.BASE_URL;

  // products
  products: Array<IProduct>;
  allProducts: Array<IProduct>;

  // families
  families: Array<IFamily>;
  allFamilies: Array<IFamily>;
  // toolbar text
  productsSelected: number = 0;

  // toolbar batch selection
  batchSelected: boolean = false;

  // show archived products
  showArchivedSelected: boolean = false;

  // Show the batch delete dialog
  showBatchDeleteModal: boolean = false;

  // Show the delete dialog
  showDeleteModal: boolean = false;

  // Delete this family when confirmed
  selectedFamilyForDeletion: IFamily;

  familySubscription: Subscription;

  productSubscription: Subscription;

  parentId: number;

  type: string;

  name: string = '';

  canWriteModules: boolean = false;
  canWriteDrivers: boolean = false;
  canViewArchiveInAdmin: boolean = false;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private admin: AdminService,
              private drilldownService: DrilldownService,
              private permissions: Permissions,
              private toast: ToastsManager) {}

  // Obsservable for search field
  private _searchChanged: Subject<string> = new Subject<string>();

  ngOnInit() {
    log.d('ngOnInit');

      // Handle debounce for search input
      this._searchChanged
          .debounceTime(300) // wait 300ms after the last event before emitting last event
          .distinctUntilChanged() // only emit if value is different from previous value
          .subscribe(name => {
              log.d('_searchChanged', name);
              this.families = this.allFamilies
                  .filter(obj => obj.name.toLowerCase().indexOf(name.toLowerCase()) !== -1);

              this.products = this.allProducts
                  .filter( obj => {
                      if ('marketingName' in obj) {
                          return obj.marketingName.toLowerCase().indexOf(name.toLowerCase()) !== -1;
                      } else {
                          // @ts-ignore
                          return obj.name.toLowerCase().indexOf(name.toLowerCase()) !== -1;
                      }
                  });
              name = '';
          });

    this.familySubscription = this.drilldownService.families.subscribe((families) => {
      log.d('drilldownService.families.subscribe', families);
      this.families = families;
      this.allFamilies = families;
      this.name = '';
    });

    this.productSubscription = this.drilldownService.products.subscribe((products) => {
        this.products = products;
        this.allProducts = products;
        this.name = '';
    });

    this.route.params.subscribe((params) => {
      log.d('params', params);
      this.parentId = +params['parentId'];
      this.type = params['type'];
      this.showArchivedSelected = Util.toBoolean(params['showArchived']);
    });

    this.permissions.hasFeature(ACLFeature.WRITE_DRIVERS)
      .subscribe((success) => {
          this.canWriteDrivers = success;
    });

    this.permissions.hasFeature(ACLFeature.WRITE_MODULES)
      .subscribe((success) => {
          this.canWriteModules = success;
    });

    this.permissions.hasFeature(ACLFeature.VIEW_ARCHIVE_IN_ADMIN)
      .subscribe((success) => {
          this.canViewArchiveInAdmin = success;
      });
  }

  ngOnDestroy() {
    log.d('ngOnDestroy');
    this.familySubscription.unsubscribe();
    this.productSubscription.unsubscribe();
  }

  public onSearchChanged($event) {
    this._searchChanged.next($event);
  }

  /**
   * Handle Click on Table Cell
   * @param data
   */
  public onFamilyCellClicked(family: IFamily) {
    const id = family.id;
    log.d('onFamilyCellClicked', family.name);
    this.router.navigate([{parentId: id, showArchived: this.showArchivedSelected}], {relativeTo: this.route} );
  }

  /**
   *
   * @param data
   */
  public onProductCellClicked(product: IProduct) {
    const id = product.id;
    log.d('onProductCellClicked', product.name);
    this.router.navigate([this.type, product.id], {relativeTo: this.route} );
  }

  /**
   *
   * @param family
   */
  public onEditFamily($event, family: IFamily) {
    $event.stopPropagation();
    log.d('onEditFamily', family.name);
    this.router.navigate(['family', family.id], {relativeTo: this.route} );
  }

  /**
   *
   * @param family
   */
  public onDeleteFamily($event, family: IFamily) {
    $event.stopPropagation();
    log.d('onDeleteFamily', family.name);
    this.selectedFamilyForDeletion = family;
    this.showDeleteModal = true;
  }

  /**
   * Handle
   * @param confirmed
   */
  public onDeleteConfirmed(confirmed) {
    log.d('onDeleteConfirmed', confirmed);
    if (confirmed) {
      this.admin.deleteFamilies(this.type, this.selectedFamilyForDeletion.id).subscribe((data) => {
        if (data.status === 'fail' || data.status === 'error'){
          this.toast.error(data.data);
        } else {
          this.toast.success(`${this.selectedFamilyForDeletion.name} has been deleted successfully.`);
          if (isNaN(this.parentId)) {
            this.parentId = null;
          }
          this.router.navigate(['/drilldown/' + this.type, {parentId: this.parentId, refresh: Date.now()}]);
        }
        this.selectedFamilyForDeletion = null;
      });
    }
    this.showDeleteModal = false;
  }

  /**
   * Handle product selection
   * @param product
   */
  public onProductSelected(product: IProduct) {
    log.d('onProductSelected', product.name);
    this.batchSelected = (this.selected === this.products.length);
  }

  /**
   * Handle product deletion
   */
  public onProductDelete() {
    log.d('onProductDelete');
  }

  /**
   * Handle batch selection for all products
   */
  public onBatchCheckAll() {
    log.d('onBatchCheckAll', this.batchSelected);
    this.products.forEach((product: IProduct) => {
      product.selected = this.batchSelected;
    });

    log.d(this.selected.toString());
  }

  /**
   * Handle batch selection edit
   */
  public onBatchEdit() {
    log.d('onBatchEdit', this.batchSelected);

    const selectedProductIds = [];

    for (const product of this.products){
      if (product.selected === true) {
        selectedProductIds.push(product.id);
      }
    }

    log.d('selectedProductIds', selectedProductIds);

    this.router.navigate(['batch', this.type, selectedProductIds.join()], {relativeTo: this.route} );
  }

  /**
   * Handle batch selection delete
   */
  public onBatchDelete() {
    log.d('onBatchDelete', this.batchSelected);
    this.showBatchDeleteModal = true;
  }

  public transformUrl(url) {
    return `url('${encodeURI(url)}')`;
  }
  /**
   * Callback to confirm batch deletion
   * @param confirmed
   */
  public onBatchDeleteConfirmed(confirmed) {
    log.d('onBatchDeleteConfirmed', confirmed);
    if (confirmed) {
      const selectedProductIds = [];
      for (const product of this.products) {
        if (product.selected === true) {
          selectedProductIds.push(product.id);
        }
      }

      this.admin.deleteBatch(this.type, selectedProductIds).subscribe(
          (data) => {
            this.toast.success('The selected products are successfully deleted.');
          },
          (err) => {
            const jsonError = JSON.parse(err._body);
            console.log(jsonError.message);
            this.toast.error('Something went wrong deleting the selected products.');
          });
      // this.router.navigate(["/drilldown/" + this.type, {parentId: this.parentId}], {relativeTo: this.route} );
    }
    this.showBatchDeleteModal = false;
  }

  public getViewStatusClass(viewStatus) {
    switch (viewStatus) {
      case 1:
        return 'edit-is-visible-in-region';
      case 2:
        return 'edit-is-in-maintenance-mode';
      case 3:
        return 'edit-is-in-archive';
      default:
        return '';
    }
  }

  /**
   * Count total number of selected items
   * @returns {number}
   */
  get selected() {
    return this.products.reduceRight((acc, product) => {
      return acc += product.selected ? 1 : 0;
    }, 0);
  }

  public toggleArchived() {
    const params = {parentId: null, showArchived: this.showArchivedSelected};
    if (!isNaN(this.parentId)) {
      params.parentId = this.parentId;
    }
    this.router.navigate(['/drilldown/' + this.type, params]);
  }
}



// WEBPACK FOOTER //
// ./src/app/drilldown/list/list.component.ts